/**
 * FooterBar.tsx - Barra inferior de navegación del wizard
 *
 * UI-F404 · Shell · 2026-03-19
 *
 * Réplica del .footer-bar de p3-coteja-v15.html adaptada a React.
 * Position: fixed · bottom: 88px (deja espacio al stepper si lo hay)
 * Slot izq: resumen numérico libre (ReactNode)
 * Slot der: botón secundario opcional + botón primario «Continuar a X»
 *
 * REGLAS:
 * - NUNCA hover glow verde (step2-no-glow.css global ya lo cubre)
 * - Sin lógica de negocio — puramente presentacional
 *
 * @version 1.0.0
 */

import { useState, type FC, type CSSProperties, type ReactNode } from 'react';
import { colors, typography, borderRadius, spacing, transitions } from '@/design-system/v2';

// ============================================================================
// TYPES
// ============================================================================

export interface FooterBarProps {
  /** Slot izquierdo: resumen numérico libre (ej: «47 geolocalizados · 3 críticos») */
  summary?: ReactNode;
  /** Texto del botón primario (ej: «Continuar a Valida →») */
  continueLabel: string;
  /** Callback del botón primario */
  onContinue: () => void;
  /** Deshabilitar botón primario */
  continueDisabled?: boolean;
  /** Texto del botón secundario opcional */
  secondaryLabel?: string;
  /** Callback del botón secundario */
  onSecondary?: () => void;
  /** Clase CSS adicional */
  className?: string;
}

// ============================================================================
// STYLES (extraídos de p3-coteja-v15.html línea ~720)
// ============================================================================

const footerBarStyle: CSSProperties = {
  position: 'fixed',
  bottom: '88px',          // deja espacio al stepper flotante
  left: 0,
  right: 0,
  background: colors.bg.secondary,
  borderTop: `1px solid ${colors.border.DEFAULT}`,
  padding: `11px ${spacing[14] ?? '56px'}`,  // alineado con content (margin 56px)
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing[4] ?? '16px',
};

const summaryStyle: CSSProperties = {
  fontSize: typography.fontSize.sm,
  color: colors.fg.muted,
  fontFamily: typography.fontFamily.mono,
  display: 'flex',
  alignItems: 'center',
  gap: spacing[2] ?? '8px',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing[3] ?? '12px',
};

const secondaryBtnBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '7px 14px',
  borderRadius: borderRadius.md,
  border: `1px solid ${colors.border.DEFAULT}`,
  background: 'transparent',
  color: colors.fg.secondary,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.sans,
  cursor: 'pointer',
  transition: transitions.base,
  fontWeight: typography.fontWeight.medium,
};

const primaryBtnBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '7px 18px',
  borderRadius: borderRadius.md,
  border: 'none',
  background: colors.primary,
  color: colors.onPrimary,
  fontSize: typography.fontSize.sm,
  fontFamily: typography.fontFamily.sans,
  cursor: 'pointer',
  transition: transitions.base,
  fontWeight: typography.fontWeight.semibold,
};

const primaryBtnDisabled: CSSProperties = {
  ...primaryBtnBase,
  opacity: 0.45,
  cursor: 'not-allowed',
};

// ============================================================================
// COMPONENT
// ============================================================================

export const FooterBar: FC<FooterBarProps> = ({
  summary,
  continueLabel,
  onContinue,
  continueDisabled = false,
  secondaryLabel,
  onSecondary,
  className,
}) => {
  const [secHover, setSecHover] = useState(false);
  const [priHover, setPriHover] = useState(false);

  const secondaryStyle: CSSProperties = {
    ...secondaryBtnBase,
    ...(secHover
      ? { borderColor: colors.border.DEFAULT, color: colors.fg.primary, background: colors.bg.hover }
      : {}),
  };

  const primaryStyle: CSSProperties = continueDisabled
    ? primaryBtnDisabled
    : {
        ...primaryBtnBase,
        ...(priHover ? { background: colors.primaryHover } : {}),
      };

  return (
    <div style={footerBarStyle} className={className} role="navigation" aria-label="Navegación de paso">
      {/* Slot izquierdo: resumen */}
      <div style={summaryStyle}>
        {summary ?? null}
      </div>

      {/* Slot derecho: acciones */}
      <div style={actionsStyle}>
        {secondaryLabel && onSecondary && (
          <button
            type="button"
            style={secondaryStyle}
            onClick={onSecondary}
            onMouseEnter={() => setSecHover(true)}
            onMouseLeave={() => setSecHover(false)}
          >
            {secondaryLabel}
          </button>
        )}
        <button
          type="button"
          style={primaryStyle}
          onClick={continueDisabled ? undefined : onContinue}
          onMouseEnter={() => !continueDisabled && setPriHover(true)}
          onMouseLeave={() => setPriHover(false)}
          disabled={continueDisabled}
          aria-disabled={continueDisabled}
        >
          {continueLabel}
        </button>
      </div>
    </div>
  );
};

export default FooterBar;
